import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { ChannelIcon } from '../../../components/ui/ChannelIcon';
import { CHANNEL_LABEL } from '../../../lib/inboxView';
import type {
  PeriodStat,
  PlatformMiniCard as MiniCardData,
} from '../../../lib/reporting/socialOverview';
import { cn } from '../../../lib/utils';

// Organic Social Reporting — one per-platform summary card (FB / IG / GBP).
// Consolidates the old Audience & Growth tab: a measurable platform shows reach /
// engagement / follower net (with a gained/lost split) / current followers, plus
// (Instagram) its total_value PERIOD TOTALS — views / accounts engaged / total
// interactions — with deltas, since IG exposes those only as totals, not daily
// lines. GBP keeps the explicit "not measurable" state + cadence. One unified
// template — never an industry branch.
export function PlatformMiniCard({ card }: { card: MiniCardData }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <ChannelIcon channel={card.platform} className="h-5 w-5" />
        <h3 className="text-sm font-semibold text-slate-900">{CHANNEL_LABEL[card.platform]}</h3>
      </div>

      {card.measurable ? (
        <div className="space-y-3">
          <dl className="grid grid-cols-2 gap-3">
            <Metric label="Reach" value={fmt(card.reach)} />
            {card.engagement !== null && <Metric label="Engagement" value={fmt(card.engagement)} />}
            <FollowerGrowthMetric card={card} />
            <Metric label="Followers" value={fmt(card.currentFollowers)} />
            <Metric label="Posts published" value={card.postsPublished.toLocaleString()} />
          </dl>

          {card.periodStats.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <p className="mb-2 text-[11px] uppercase tracking-wide text-slate-400">
                This period
              </p>
              <dl className="grid grid-cols-3 gap-3">
                {card.periodStats.map((stat) => (
                  <PeriodStatCell key={stat.key} stat={stat} />
                ))}
              </dl>
            </div>
          )}
        </div>
      ) : card.gbpStats ? (
        // Google Business: real KPIs (total impressions / calls / directions /
        // website clicks with deltas) + cadence — no longer "not measurable".
        <div className="space-y-3">
          <dl className="grid grid-cols-2 gap-3">
            {card.gbpStats.map((stat) => (
              <PeriodStatCell key={stat.key} stat={stat} />
            ))}
            <Metric label="Posts published" value={card.postsPublished.toLocaleString()} />
          </dl>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
            {card.note ?? 'Not measurable'}
          </p>
          <dl className="grid grid-cols-2 gap-3">
            <Metric label="Posts published" value={card.postsPublished.toLocaleString()} />
          </dl>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="text-base font-semibold tabular-nums text-slate-900">{value}</dd>
    </div>
  );
}

// Follower growth net, with the gained / lost split beneath it when available.
function FollowerGrowthMetric({ card }: { card: MiniCardData }) {
  const hasSplit = card.followersGained !== null || card.followersLost !== null;
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">Follower growth</dt>
      <dd className="text-base font-semibold tabular-nums text-slate-900">{fmtSigned(card.followerNet)}</dd>
      {hasSplit && (
        <p className="mt-0.5 text-[11px] tabular-nums text-slate-400">
          <span className="text-emerald-600">+{Math.round(card.followersGained ?? 0).toLocaleString()}</span>
          {' / '}
          <span className="text-red-500">−{Math.round(card.followersLost ?? 0).toLocaleString()}</span>
        </p>
      )}
    </div>
  );
}

// A total_value period metric with its delta chip (IG views / accounts engaged /
// total interactions).
function PeriodStatCell({ stat }: { stat: PeriodStat }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">{stat.label}</dt>
      <dd className="flex items-baseline gap-1 text-base font-semibold tabular-nums text-slate-900">
        {Math.round(stat.current).toLocaleString()}
        <DeltaChip stat={stat} />
      </dd>
    </div>
  );
}

function DeltaChip({ stat }: { stat: PeriodStat }) {
  if (stat.deltaPct === null || stat.direction === null) return null;
  const Icon = stat.direction === 'up' ? ArrowUpRight : stat.direction === 'down' ? ArrowDownRight : Minus;
  const tone =
    stat.direction === 'up' ? 'text-emerald-600' : stat.direction === 'down' ? 'text-red-500' : 'text-slate-400';
  return (
    <span className={cn('inline-flex items-center text-[11px] font-medium', tone)}>
      <Icon className="h-3 w-3" aria-hidden />
      {stat.deltaPct > 0 ? '+' : ''}
      {stat.deltaPct.toFixed(0)}%
    </span>
  );
}

// null → em dash (never 0) — the same honesty rule as the hero cards.
function fmt(v: number | null): string {
  return v === null ? '—' : Math.round(v).toLocaleString();
}
function fmtSigned(v: number | null): string {
  if (v === null) return '—';
  return `${v > 0 ? '+' : ''}${Math.round(v).toLocaleString()}`;
}
